<template>
  <div
    :class="{ 'column': $q.screen.lt.sm }"
    data-test-id="item-detail"
  >
    <q-banner
      v-if="$register && orderItem.hasCustomPrice"
      dense
      rounded
      class="bg-orange text-white col-12 q-mb-sm"
    >
      <template #avatar>
        <q-icon name="warning" />
      </template>
      This item allows custom pricing. Please confirm the price below.
    </q-banner>
    <slot name="item-variation-buttons">
      <!-- TODO OOS INDY-6757 Update this to use availableItemVariations (sub-component filters prop personally) -->
      <item-variation-buttons
        class="col"
        :class="{
          'border-bottom q-pa-md': $q.screen.lt.sm || !$isLandscapeView(),
          'q-mb-lg': !$q.screen.lt.sm && $isLandscapeView()
        }"
        :order-item="orderItem"
        :item-order-item="orderItem.itemOrderItem"
        :round="round"
        :item-variations="item?.itemVariations?.data"
        @edit-options-button-click="toggleEditModeCallback => handleToggleEditMode(toggleEditModeCallback)"
        @request-authorization="callback => $emit('request-authorization', callback)"
      />
    </slot>
    <slot
      name="item-modifier-sets"
      :item-modifier-sets="availableItemModifierSets"
      :available-online="availableOnline"
    >
      <item-modifier-set
        v-for="itemModifierSet in availableItemModifierSets"
        :key="itemModifierSet.id"
        class="col"
        :class="{
          'border-bottom q-pa-md': $q.screen.lt.sm || !$isLandscapeView(),
          'q-mb-lg': !$q.screen.lt.sm && $isLandscapeView()
        }"
        :order-item="orderItem"
        :item-order-item="orderItem.itemOrderItem"
        :item-modifier-set="itemModifierSet"
        :available-online="availableOnline"
        :round="round"
        @edit-options-button-click="toggleEditModeCallback => handleToggleEditMode(toggleEditModeCallback)"
        @request-authorization="callback => $emit('request-authorization', callback)"
      />
    </slot>

    <div
      v-if="$register && orderItem.hasCustomPrice"
      class="row"
      :class="{
        'border-bottom q-pa-md': $q.screen.lt.sm || !$isLandscapeView(),
        'q-mb-lg': !$q.screen.lt.sm && $isLandscapeView()
      }"
    >
      <q-input
        ref="customPriceAmount"
        v-model.number="orderItem.amount"
        :class="['col-4', 'q-mr-lg', isHighlightingPriceField && 'highlighted-outline']"
        label="Custom Price Amount"
        label-color="orange"
        :prefix="currentCircuit.localeCurrencySymbol"
        type="number"
        step="0.01"
        min="0"
        :rules="[val => $_.isNumber(val) || 'Cannot be blank', val => val >= 0 || 'Cannot be negative']"
        clearable
      />
      <q-btn
        v-for="amount in quickCashAmountOptions"
        :key="amount"
        class="q-px-sm q-mx-xs q-mt-sm q-py-xs quick-cash-button"
        color="green"
        outline
        dense
        @click="addAmount(amount)"
      >
        +
        <i18n-n
          :value="amount"
          :format="Math.round(amount) === amount ? 'currencyNoCents' : 'currency'"
          scope="global"
        />
      </q-btn>
    </div>

    <div
      class="col"
      :class="{
        'border-bottom q-pa-md': $q.screen.lt.sm || !$isLandscapeView(),
        'q-my-lg': !$q.screen.lt.sm && $isLandscapeView()
      }"
    >
      <q-input
        v-model="orderItem.note"
        type="textarea"
        label="Notes"
        :rounded="round"
      />
    </div>

    <div
      v-if="$register || orderItem.discount"
      class="q-my-sm row justify-between items-center q-gutter-x-md"
      :class="$q.screen.xs && $register ? 'q-px-md' : undefined"
    >
      <div class="text-h5">
        Discounts
      </div>
      <q-toggle
        v-if="$register"
        :model-value="!orderItem.manualDiscount"
        label="Apply Automatically"
        data-test-id="discount-toggle"
        @update:model-value="toggleManualDiscount(orderItem)"
      />
    </div>

    <div
      class="q-mb-sm row q-gutter-md"
      :class="$q.screen.xs ? 'q-px-md q-pb-md border-bottom' : undefined"
    >
      <q-chip
        v-if="!$_.isNil(orderItem.discount)"
        dense
        color="secondary"
        text-color="white"
        :size="$q.screen.xs ? 'lg' : 'xl'"
        :label="orderItem.discount.name"
        :removable="$register"
        @remove="addManualDiscount(orderItem, null)"
      />
      <q-select
        v-else-if="$register && orderItem.manualDiscount"
        :model-value="orderItem.discountId"
        style="min-width: 200px;"
        label="Discount"
        :options="itemDiscounts"
        option-label="name"
        :display-value="orderItem.discount && orderItem.discount.displayName || 'None'"
        data-test-id="discount-select"
        @update:model-value="selectedDiscount => addManualDiscount(orderItem, selectedDiscount)"
      />
    </div>

    <template v-if="$register">
      <div
        class="q-my-sm row justify-between items-center q-gutter-x-md"
        :class="$q.screen.xs ? 'q-px-md' : undefined"
      >
        <div
          class="text-h5 col-auto"
          :class="orderItem.hasDueTime"
        >
          Due Time
        </div>

        <q-toggle
          :model-value="orderItem.hasDueTime !== null"
          label="Override"
          @update:model-value="value => toggleHasDueTimeOverride(value)"
        />
      </div>
      <div
        class="q-mb-sm row q-gutter-md items-center"
        :class="$q.screen.xs ? 'q-px-md q-pb-md border-bottom' : undefined"
      >
        <q-select
          label="Pickup"
          class="col text-uppercase"
          :label-color="orderItem.hasDueTime ? 'white' : undefined"
          :bg-color="orderItem.hasDueTime ? 'primary' : undefined"
          :model-value="optionValue"
          :options="[
            { value: DUE_TIME_OPTIONS.NOW, label: 'Now' },
            { value: 10, label: '10 Min' },
            { value: 20, label: '20 Min' },
            { value: 30, label: '30 Min' },
            { value: 60, label: '40 Min' },
            { value: DUE_TIME_OPTIONS.CUSTOM, label: 'Custom Time and Date' },
            { value: DUE_TIME_OPTIONS.HOLD, label: 'Hold' }]"
          emit-value
          @update:model-value="value => setDueTimeOptionAndRelativeDueTime(value)"
        >
          <template #prepend>
            <q-icon
              name="access_time"
              :color="orderItem.hasDueTime ? 'white' : undefined"
            />
          </template>
          <template
            v-if="orderItem.hasDueTime"
            #after
          >
            <q-icon
              name="info"
              color="warning"
            />
          </template>
          <template
            v-if="orderItem.hasDueTime"
            #selected
          >
            <q-chip
              color="white"
              text-color="dark"
              icon="alarm"
              style="margin-left: -10px; font-weight: bolder;"
            >
              {{ optionValue }}
            </q-chip>
          </template>
        </q-select>
      </div>
      <div
        v-if="orderItem.order.futureShowings.length"
        class="column q-gutter-sm items-start q-mt-sm"
        style="margin-left: 2px;"
      >
        <div
          v-for="showing in orderItem.order.futureShowings"
          :key="showing.id"
          class="row q-gutter-md items-start q-mb-md"
        >
          <span
            class="text-h6 ellipsis"
            style="max-width: 193px;"
          >{{ showing.movie.name }}</span>
          <q-btn
            v-for="deliveryTime in showing.suggestedDeliveryTimes"
            :key="deliveryTime.minutes"
            class="col-auto"
            style="width: 84px;"
            dense
            outline
            no-caps
            @click="setDueTime(deliveryTime.time)"
          >
            <div class="column full-width">
              <div
                class="col-auto text-caption text-left"
                style="font-size: 8px; line-height: 0.5rem;"
              >
                {{ deliveryTime.label }}
              </div>
              <div class="col-auto text-center text-uppercase">
                {{ $d(deliveryTime.time.inTimeZone(currentSite.timeZone), 'shortTime') }}
              </div>
              <div
                class="col-auto text-caption text-right"
                style="font-size: 8px; line-height: 0.5rem;"
              >
                {{ deliveryTime.minutes }} min
              </div>
            </div>
          </q-btn>
        </div>
      </div>
    </template>
    <div
      v-if="$register && !hideDeliveryType"
      class="q-my-sm row justify-between"
      :class="$q.screen.xs ? 'q-px-md' : undefined"
    >
      <div class="text-h5">
        Delivery
      </div>
      <q-toggle
        :model-value="!!orderItem.deliveryType || !!orderItem.deliveryLocation || !!orderItem.deliveryPositions.length"
        label="Override"
        @update:model-value="value => toggleDeliveryOverride(value)"
      />
    </div>
    <template v-if="$register && !hideDeliveryType">
      <div
        class="q-my-sm row"
        :class="$q.screen.xs ? 'q-px-md q-pb-md border-bottom' : undefined"
        style="max-width: 100%;"
      >
        <template v-if="orderItem.deliveryTypeOptions.length">
          <q-select
            v-if="$q.screen.xs"
            label="Delivery Position"
            class="col"
            :model-value="orderItem.deliveryTypeForPlacing"
            :options="$_.map(orderItem.deliveryTypeOptions, deliveryTypeOption => ({ label: deliveryTypeOption.label, value: deliveryTypeOption.value }))"
            map-options
            emit-value
            @update:model-value="value => orderItem.deliveryType = value"
          />
          <q-btn-toggle
            v-else
            :model-value="orderItem.deliveryTypeForPlacing"
            spread
            no-caps
            unelevated
            size="lg"
            class="col"
            toggle-color="primary"
            color="white"
            text-color="primary"
            style="border: 1px solid var(--q-primary)"
            :options="$_.map(orderItem.deliveryTypeOptions, deliveryTypeOption => ({ label: deliveryTypeOption.label, value: deliveryTypeOption.value }))"
            @update:model-value="value => orderItem.deliveryType = value"
          />
        </template>
        <q-btn-toggle
          v-if="!orderItem.deliveryTypeOptions.length"
          disable
          :model-value="false"
          no-caps
          rounded
          toggle-color="primary"
          unelevated
          color="white"
          text-color="primary"
          :options="[{ label: 'N/A', value: true }]"
        />
      </div>
      <div
        class="row q-my-sm q-gutter-md"
        :class="{ 'q-px-md column': $q.screen.xs }"
      >
        <q-input
          v-if="orderItem.collectNameForOrder"
          v-model="orderItem.nameForOrder"
          label="Name for Order"
          :class="$q.screen.xs ? 'col-12' : 'col-3 q-pl-md'"
        />
        <q-input
          v-if="orderItem.collectPagerNumber"
          v-model="orderItem.pagerNumber"
          label="Pager Number"
          type="number"
          :class="$q.screen.xs ? 'col-12' : 'col-3 q-pl-md'"
        />
        <q-input
          v-if="orderItem.customDeliveryType && orderItem.customDeliveryType.hasDetails"
          v-model="orderItem.deliveryDetails"
          :class="$q.screen.xs ? 'col-12' : 'col-6 q-pl-md'"
          :label="orderItem.customDeliveryType.detailsFieldName"
          :rules="[val => !orderItem.customDeliveryType.requireDetails || !!val || $t('form.errors.required')]"
        />
        <q-select
          v-if="(orderItem.deliveryType && (orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE || orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT)) || (!orderItem.deliveryType && orderItem.defaultDeliveryType && (orderItem.defaultDeliveryType === DELIVERY_TYPE_OPTIONS.TABLE || orderItem.defaultDeliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT))"
          :model-value="orderItem.deliveryLocation || orderItem.defaultDeliveryLocation"
          :class="$q.screen.xs ? 'col-3' : 'col-6 q-pl-md'"
          :label="orderItem.deliveryLocationLabel"
          :options="$_.map(deliveryLocationOptions, 'name')"
          use-input
          input-debounce="50"
          map-options
          emit-value
          clearable
          @filter="filterDeliveryLocationOptions"
          @update:model-value="selectDeliveryLocation"
        />
      </div>
      <div
        v-if="!hideDeliveryType && orderItem.deliveryPositionOptions.length && (orderItem.deliveryType && (orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE || orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT)) || (!orderItem.deliveryType && orderItem.defaultDeliveryType && (orderItem.defaultDeliveryType === DELIVERY_TYPE_OPTIONS.TABLE || orderItem.defaultDeliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT))"
        class="row"
        :class="$q.screen.xs ? 'q-px-md q-col-gutter-sm' : 'q-col-gutter-lg'"
      >
        <div
          v-for="position in orderItem.deliveryPositionOptions"
          :key="position"
          class="col-2"
        >
          <q-btn
            class="bg-responsive-grey-3 full-width"
            :label="position"
            :size="$q.screen.xs ? 'md' : 'xl'"
            no-caps
            :class="{
              'text-white bg-primary': ($_.includes(deliveryPositions, position)),
              [$getTextColor(currentSite, 'primary')]: ($_.includes(deliveryPositions, position))
            }"
            @click="selectDeliveryPosition(position)"
          />
        </div>
      </div>
    </template>

    <q-dialog v-model="showDueTimeDialog">
      <q-card>
        <q-card-section class="q-gutter-md row items-start">
          <q-date
            :model-value="orderItem.dueTime && date.formatDate(orderItem.dueTime, 'YYYY-MM-DDTHH:mm:ssZ')"
            flat
            mask="YYYY-MM-DDTHH:mm:ssZ"
            :options="validDateOption"
            @update:model-value="value => setDueTime(new Date(value))"
          />
          <q-time
            :model-value="orderItem.dueTime && date.formatDate(orderItem.dueTime, 'YYYY-MM-DDTHH:mm:ssZ')"
            flat
            mask="YYYY-MM-DDTHH:mm:ssZ"
            :options="validTimeOption"
            @update:model-value="value => setDueTime(new Date(date.formatDate(orderItem.dueTime, 'YYYY-MM-DD') + date.formatDate(value, 'THH:mm:ss') + date.formatDate(orderItem.dueTime, 'Z')))"
          />
        </q-card-section>
        <q-card-actions class="row justify-end q-ma-sm">
          <q-btn
            v-close-popup
            label="Done"
            color="primary"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <action-dialog
      ref="openTabActionDialog"
      title="Found Open Tab For Table"
      :title-chip="foundOrder ? 'Tab Id ' + foundOrder.id : null"
      :message="foundOrderDialogMessage"
      ok-label="Continue New Order"
      cancel-label="Use Open Tab"
      @ok="continueOrder"
      @cancel="mergeOrders"
    />
  </div>
</template>

<script>
import { date } from 'quasar'
import { useAddManualDiscount } from 'src/composables/order/redemptions/discounts/AddManualDiscount'
import { useRemoveManualDiscount } from 'src/composables/order/redemptions/discounts/RemoveManualDiscount'
import { isPos } from 'src/config'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { IsoDateTime } from 'src/models/model'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { useAvailableItemModifierSets } from 'src/modules/checkout/composables/sales-availability/items/AvailableItemModifierSets'
import { useSalesChannel } from 'src/modules/checkout/composables/sales-channels/SalesChannel'
import ActionDialog from 'src/modules/consumer/components/ActionDialog'
import ItemModifierSet from 'src/modules/register/components/order-item-form/components/ItemModifierSet'
import ItemVariationButtons from 'src/modules/register/components/order-item-form/components/ItemVariationButtons'

const dateThreshold = date.formatDate(new Date(), 'YYYY/MM/DD')

const DUE_TIME_OPTIONS = {
  HOLD: 'hold',
  FUTURE: 'future',
  NOW: 'now'
}

const CONTEXT = isPos ? ['currentUser', 'currentRegisterClient'] : null

export default {
  name: 'ItemDetail',
  components: {
    ItemVariationButtons,
    ItemModifierSet,
    ActionDialog
  },
  context: CONTEXT,
  configuration: ['currentCircuit', 'currentSite', 'deliveryLocations', 'discounts'],
  props: {
    orderItem: {
      type: Object,
      required: true
    },
    hideDeliveryType: {
      type: Boolean,
      required: false,
      default: false
    },
    availableOnline: {
      type: Boolean,
      required: false,
      default: false
    },
    round: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  models: {
    orders: {
      variables () {
        return {
          state: 'open_tab',
          deliveryLocation: this.deliveryLocation,
          orderToExclude: this.order.isRemote ? this.order.id : null,
          limit: 1,
          skip: true
        }
      },
      immediate: false
    }
  },
  emits: ['request-authorization', 'merge-tab'],
  setup (props) {
    const { salesChannel } = useSalesChannel(
      SALES_CHANNEL_TYPES.MENU,
      props.orderItem?.order
    )

    const { availableItemModifierSets } = useAvailableItemModifierSets(
      props.orderItem?.itemOrderItem?.item?.itemModifierSets?.data,
      {
        salesChannel,
        orderItem: props.orderItem
      }
    )

    const { addManualDiscount } = useAddManualDiscount()
    const { removeManualDiscount } = useRemoveManualDiscount()

    return {
      availableItemModifierSets,
      addManualDiscount,
      removeManualDiscount
    }
  },
  data () {
    return {
      showDueTimeDialog: false,
      deliveryLocationOptions: [],
      deliveryLocation: null,
      loading: false,
      date,
      isHighlightingPriceField: false,
      itemVariationPrice: null,
      adminApproved: false
    }
  },
  computed: {
    item () {
      return this.orderItem.itemOrderItem.item
    },
    order () {
      return this.orderItem.order
    },
    deliveryPositions () {
      // Check if order item delivery is being overridden
      if (!!this.orderItem.deliveryType || !!this.orderItem.deliveryLocation || !!this.orderItem.deliveryPositions.length) {
        return this.orderItem.deliveryPositions
      } else {
        return this.orderItem.defaultDeliveryPositions
      }
    },
    itemDiscounts () {
      return _.filter(this.discounts.data, { type: 'item' })
    },
    buttonValue () {
      if (this.orderItem.hasDueTime && this.orderItem.dueTime !== null) {
        return DUE_TIME_OPTIONS.FUTURE
      } else if (this.orderItem.hasDueTime) {
        return DUE_TIME_OPTIONS.HOLD
      } else {
        return DUE_TIME_OPTIONS.NOW
      }
    },
    optionValue () {
      if (this.orderItem.hasDueTime && this.orderItem.dueTime !== null) {
        return this.orderItem.formattedDueTime
      } else if (this.orderItem.hasDueTime) {
        return DUE_TIME_OPTIONS.HOLD
      } else {
        return DUE_TIME_OPTIONS.NOW
      }
    },
    foundOrder () {
      return _.first(this.orders?.data)
    },
    foundOrderDialogMessage () {
      const foundOrderName = this.foundOrder?.nameForOrder || this.foundOrder?.name ? `"${this.foundOrder?.nameForOrder || this.foundOrder?.name}" ` : ''
      const foundOrderLocation = this.foundOrder?.deliveryLocation ? `location ${this.foundOrder?.deliveryLocation}` : 'the selected location'

      return `Tab ${foundOrderName}is already open for ${foundOrderLocation}. Create a new order or use the existing open tab.`
    },
    quickCashAmountOptions () {
      const options = this.currentRegisterClient?.registerStation?.registerType?.quickCashAmountOptions || []
      return options.length > 0 ? options : [20, 10, 5, 1]
    }
  },
  watch: {
    'orderItem.deliveryType' () {
      if (this.orderItem.deliveryLocation) {
        const deliveryLocation = _.find(this.deliveryLocations.data, deliveryLocation => {
          return deliveryLocation === this.orderItem.deliveryLocation
        })

        if (deliveryLocation && deliveryLocation?.type !== this.orderItem.deliveryType) {
          this.orderItem.deliveryLocation = null
          this.orderItem.resetDeliveryPositions()
        }
      }
    },
    'orderItem.deliveryLocation' () {
      if (this.orderItem.deliveryPositions?.length) {
        const deliveryLocation = _.find(this.deliveryLocations.data, deliveryLocation => {
          return deliveryLocation === (this.orderItem.deliveryLocation || this.orderItem.defaultDeliveryLocation)
        })

        if (deliveryLocation && this.deliveryPositions.length && (_.without(this.orderItem.deliveryPositions, ...deliveryLocation.positions)).length !== 0) {
          this.orderItem.resetDeliveryPositions()
        }
      }
    },
    'orderItem.itemOrderItem.price' (newPrice) {
      if (this.orderItem.hasCustomPrice && _.isNumber(newPrice)) {
        this.orderItem.amount = newPrice
        this.flashCustomPriceField()
      }
    }
  },
  created () {
    _.extend(this, { DUE_TIME_OPTIONS, DELIVERY_TYPE_OPTIONS })
  },
  mounted () {
    if (this.orderItem.hasCustomPrice) {
      setTimeout(this.flashCustomPriceField, 200)
    }
  },
  methods: {
    handleToggleEditMode (toggleEditModeCallback) {
      if (this.currentUser.admin || this.adminApproved) {
        toggleEditModeCallback?.()
      } else {
        this.$emit('request-authorization', () => {
          this.adminApproved = true
          toggleEditModeCallback?.()
        })
      }
    },
    flashCustomPriceField () {
      if (this.isHighlightingPriceField) return
      const stayHighlightedForMs = 800
      this.isHighlightingPriceField = true
      setTimeout(() => {
        this.isHighlightingPriceField = false
      }, stayHighlightedForMs)
    },
    addAmount (amount) {
      if (this.orderItem.hasCustomPrice) {
        this.orderItem.amount += amount
        this.flashCustomPriceField()
      }
    },
    filterDeliveryLocationOptions (value, update) {
      if (this.orderItem.deliveryLocationOptions) {
        if (value === '') {
          update(() => {
            this.deliveryLocationOptions = this.orderItem.deliveryLocationOptions
          })
        } else {
          update(() => {
            this.deliveryLocationOptions = _.filter(this.orderItem.deliveryLocationOptions, deliveryLocation => {
              return deliveryLocation.name.includes(value)
            })
          })
        }
      }
    },
    continueOrder () {
      this.orderItem.deliveryType = this.orderItem.deliveryType || this.orderItem.defaultDeliveryType
      this.orderItem.deliveryLocation = this.deliveryLocation
    },
    mergeOrders () {
      this.orderItem.deliveryType = this.orderItem.deliveryType || this.orderItem.defaultDeliveryType
      this.orderItem.deliveryLocation = this.deliveryLocation
      this.orderItem.order.nameForOrder ||= this.foundOrder.nameForOrder
      this.$emit('merge-tab', this.foundOrder)
    },
    async selectDeliveryLocation (deliveryLocation) {
      this.deliveryLocation = deliveryLocation

      if (deliveryLocation === null ||
          !_.includes(['draft', 'open_tab'], this.orderItem.order.state) ||
          !!this.orderItem.order.deliveryLocation ||
          (this.orderItem.deliveryTypeForPlacing || this.orderItem.defaultDeliveryType) !== DELIVERY_TYPE_OPTIONS.TABLE ||
          !this.$register) {
        this.orderItem.deliveryType = this.orderItem.deliveryType || this.orderItem.defaultDeliveryType
        this.orderItem.deliveryLocation = deliveryLocation
        return
      }

      this.loading = true

      await this.$nextTick()
      this.orders.options.variables.deliveryLocation = deliveryLocation
      await this.orders.fetch(true)

      if (this.orders.data.length > 0) {
        this.$refs.openTabActionDialog.show()
      } else {
        this.orderItem.deliveryType = this.orderItem.deliveryType || this.orderItem.defaultDeliveryType
        this.orderItem.deliveryLocation = deliveryLocation
      }

      this.loading = false
    },
    selectDeliveryPosition (position) {
      if (this.orderItem.deliveryPositions.includes(position)) {
        const index = this.orderItem.deliveryPositions.indexOf(position)
        this.orderItem.deliveryPositions.splice(index, 1)
        this.orderItem.deliveryPositions.sort()
      } else {
        this.orderItem.deliveryPositions.push(position)
        this.orderItem.deliveryPositions.sort()
      }
    },
    toggleDeliveryOverride (newToggleValue) {
      if (newToggleValue) {
        this.orderItem.deliveryType = this.orderItem.defaultDeliveryType
        this.orderItem.deliveryLocation = this.orderItem.defaultDeliveryLocation
        this.orderItem.deliveryPositions = [...this.orderItem.defaultDeliveryPositions]
      } else {
        this.orderItem.deliveryType = null
        this.orderItem.deliveryLocation = null
        this.orderItem.deliveryPositions = []
      }
    },
    setDueTimeOptionAndRelativeDueTime (option) {
      if (option === DUE_TIME_OPTIONS.NOW) {
        this.orderItem.dueTime = null
        this.orderItem.hasDueTime = false
      } else if (option === DUE_TIME_OPTIONS.HOLD) {
        this.orderItem.hasDueTime = true
        this.orderItem.dueTime = null
      } else if (option === DUE_TIME_OPTIONS.CUSTOM) {
        this.orderItem.hasDueTime = true
        this.showDueTimeDialog = true
      } else {
        this.orderItem.hasDueTime = true
        this.setRelativeDueTime(option || 30)
      }
    },
    setDueTimeOption (option) {
      if (option === DUE_TIME_OPTIONS.FUTURE) {
        this.orderItem.hasDueTime = true
        this.setRelativeDueTime(this.order.dueTime || 30)
      } else if (option === DUE_TIME_OPTIONS.NOW) {
        this.orderItem.dueTime = null
        this.orderItem.hasDueTime = false
      } else {
        this.orderItem.hasDueTime = true
        this.orderItem.dueTime = null
      }
    },
    setDueTime (time) {
      this.orderItem.hasDueTime = true
      this.orderItem.dueTime = time
      this.orderItem.relativeDueTimeMinutes = null
    },
    toggleHasDueTimeOverride (newToggleValue) {
      if (newToggleValue) {
        this.orderItem.hasDueTime = this.orderItem.defaultHasDueTime
        this.orderItem.dueTime = this.order.dueTime
      } else {
        this.orderItem.hasDueTime = null
        this.orderItem.relativeDueTimeMinutes = null
      }
    },
    setRelativeDueTime (minutes) {
      this.orderItem.hasDueTime = true
      this.orderItem.dueTime = this.timeForMinutes(minutes)
      this.orderItem.relativeDueTimeMinutes = minutes
    },
    timeForMinutes (minutes) {
      return new IsoDateTime(date.addToDate(new Date(), { minutes }))
    },
    validDateOption (date) {
      return date >= dateThreshold
    },
    validTimeOption (hour, minute) {
      if (date.formatDate(this.orderItem.dueTime, 'YYYY/MM/DD') > dateThreshold) {
        return true
      }

      const minutes = hour * 60 + minute
      const now = new Date()
      const nowMinutes = now.getHours() * 60 + now.getMinutes()

      return minutes > nowMinutes + 10
    },
    toggleManualDiscount (orderItem, selectedDiscount = null) {
      if (orderItem.manualDiscount) {
        this.performWithAuthorization(() => {
          this.removeManualDiscount(orderItem)
          orderItem.order.applyRedemptions()
        })
      } else {
        this.performWithAuthorization(() => {
          this.addManualDiscount(orderItem, selectedDiscount)
          orderItem.order.applyRedemptions()
        })
      }
    },
    performWithAuthorization (perform) {
      if (this.$register && !this.currentUser.admin) {
        this.$emit('request-authorization', () => perform())
      } else {
        perform()
      }
    }
  }
}
</script>

<style lang="scss">
.quick-cash-button {
  height: 44px;
}
.highlighted-outline .q-field__control:after {
  border-width: 2px;
  border-color: $orange;
}
</style>
